// Shared jsPDF helpers used by every PDF generator in the app (Reports,
// Invoices, Receipts, Case Analysis, Discovery, Trust, ConflictChecker,
// BatchInvoicing, …).  Before this file existed each generator had its own
// firm-header layout at slightly different y-coords with slightly different
// font sizes and no page-number footer at all, so multi-page PDFs shipped to
// clients had no "Page 3 of 5" and firms with the same letterhead across
// four documents printed at four different sizes.
//
// The goal is NOT to lock every generator into an identical template —
// invoices, receipts, and the MSJ brief have their own layouts that need
// custom typography.  The goal is to give every generator a common
// starting point for the firm letterhead + title block and the trailing
// page-number footer so those two universal surfaces don't drift again.
//
// All helpers here take jsPDF's `doc` as their first argument by convention
// (matches jspdf-autotable) and return the new y-cursor when useful so the
// caller can chain content after the header.
//
// ═══ THE PLAIN (AMICUS-STYLE) LOOK — firm decision 2026-10-13 ═══════════════
// Every PDF and printout must print and SCAN cleanly, like Amicus Attorney's
// plain reports: white page, BLACK text, a LIGHT-gray table header row with
// black bold text, thin gray rules between rows, no dark fills, no coloured
// text, no zebra striping, no charts (except AttorneyProductivity's Billable
// Time Chart, drawn in grays).  Helvetica throughout.  The helpers below are
// the ONE implementation of that look; generators use them, never a copy.
//
// Which header/footer a document gets:
//   • INTERNAL reports/printouts (Reports, Attorney Productivity, matter
//     print-outs, roles/team/firm-config, accounting statements, case
//     analysis, courtsheet, conflict check, discovery status, trust journals,
//     witness list …):
//         let y = pdfReportBox(doc, { firmSettings, title, owner, subtitle })
//         … content …
//         pdfPlainFooter(doc, { printedBy: pdfPrintedByName(user), firmSettings })
//   • CLIENT-FACING documents (invoices, receipts, settlement statement,
//     annual client trust statement, signed engagement letter):
//         let y = pdfPlainLetterhead(doc, { firmSettings, title, subtitle })
//         … content …
//         pdfPlainFooter(doc, { note })          // NO printedBy → "Page: N of M" only
//   • COURT / opposing-counsel documents keep pdfHeader + pdfFooter and their
//     own layout; only their colours change (PDF_PLAIN.text, PDF_PLAIN.watermark).
//   • The trust CHECK (Trust.js printTrustCheck, lib/checkPrintLayout.js) is
//     out of scope — physical check stock.  Never route it through these.
//
// API (all sizes mm; the helpers assume a jsPDF doc in mm, any page size):
//   PDF_PLAIN            palette + rule widths: text [0,0,0], headFill
//                        [221,221,221], rule [150,150,150], muted [85,85,85]
//                        (secondary text that must still scan), border,
//                        watermark [175,175,175] (court DRAFT stamp),
//                        chartSeries (three grays, darkest first), font
//                        'helvetica', rowRule / footRule / boxBorder widths.
//   PDF_BOTTOM_MARGIN    24 — content (tables, manual text) stops this far
//                        above the bottom edge; the footer band lives below.
//   PDF_FOOTER_BASELINE  10 — baseline of the "Printed by" / "Page:" line,
//                        measured up from the bottom edge.
//   plainTableStyles(o)  spread into autoTable options:
//                          autoTable(doc, { ...plainTableStyles({ fontSize: 8 }),
//                                           startY: y, head, body, foot, columnStyles })
//                        Pass style OVERRIDES THROUGH THE HELPER
//                        (o.styles / o.headStyles / o.bodyStyles / o.footStyles
//                        / o.margin / o.cellPadding) — a `styles:` key written
//                        after the spread REPLACES the whole object and drops
//                        the black text, the rules and the bottom margin; one
//                        written before it is silently ignored.  pdf.test.js
//                        parses every file under src/ and FAILS on an object
//                        literal that spreads plainTableStyles() (or a const
//                        holding its result) and also sets theme / styles /
//                        headStyles / bodyStyles / footStyles /
//                        alternateRowStyles / margin itself.
//                        margin.bottom is never allowed below PDF_BOTTOM_MARGIN.
//                        Per-side lineWidth ({ bottom }) is verified to work in
//                        jspdf-autotable 5.0.7 (drawCellBorders).
//   pdfReportBox(doc, { firmSettings, title, owner, subtitle, startY })
//                        → y below the box.  Thin black centred box: firm name
//                        (italic), "<owner>'s" (when owner given), title (bold,
//                        wraps), subtitle (bold; a string or an array of lines).
//   pdfPlainLetterhead(doc, { firmSettings, title, subtitle, startY, align })
//                        → y.  Black letterhead: firm name bold, address,
//                        "City, ST ZIP", "Tel: … | Fax: … | email" (each part
//                        only when set — no fax, no "Fax:"), thin black rule,
//                        title bold (wraps), subtitle (string or array).
//                        align 'center' (default) or 'left'.
//   pdfPlainFooter(doc, { printedBy, note, firmTimeZone, firmSettings, printedAt })
//                        CALL LAST, after every page exists.  Every page:
//                        right "Page: N of M"; left, for INTERNAL documents,
//                        "Printed by <name> on <Weekday, Month d, yyyy h:mm AM>"
//                        in the FIRM time zone.  printedBy decides which:
//                          • omitted / null / undefined / false → CLIENT footer;
//                          • a string or a user object → INTERNAL.  An EMPTY
//                            name (user not loaded yet: pdfPrintedByName(undefined)
//                            is '') still stamps "Printed on <time>" — an internal
//                            report never silently turns into a client footer.
//                        A stamp too wide for its side shrinks to clear "Page:".
//                        Optional black note centred above the stamp line:
//                        wrapped to the page width at 8pt, stepping down to 7,
//                        6.5 and 6pt until it fits the band below the content
//                        limit (3 lines, or 4 at 6pt).  Words are NEVER dropped:
//                        a note too long even then (~650+ characters on
//                        portrait A4) prints every line, rises above the band,
//                        and console.warn()s — keep footer notes short.
//   pdfPrintedByName(user)   user.name || user.email || ''.
//   pdfPrintedStamp(date, tz) "Tuesday, October 13, 2026 2:15 PM" — built from
//                        Intl parts so ICU's " at " / narrow no-break space
//                        never reach jsPDF (they would flip the run to UTF-16).
//   pdfResolveTimeZone({ firmTimeZone, firmSettings }) → first VALID of:
//                        firmTimeZone, firmSettings.timezone,
//                        window.__lextrackFirmTz (utils/eventTime firmTz()),
//                        America/Los_Angeles.  Never the browser's zone.
//   pdfContentBottom(doc)      page height − PDF_BOTTOM_MARGIN.
//   pdfEnsureSpace(doc, y, needed, { top })  manual page-break check against
//                        pdfContentBottom; adds a page and returns `top` (20)
//                        when `needed` mm will not fit below y.
//   pdfPlainReset(doc)   helvetica normal, body size, black text and draw
//                        colour — call after anything that coloured the pen.
//
// jsPDF TRAP every generator must respect: jsPDF sets the TEXT colour with
// the fill-colour operator and never isolates it, so a rect(…, 'F') drawn
// after any doc.text() is painted in the TEXT colour (black) unless
// setFillColor() is called IMMEDIATELY before it.  Always set the fill on the
// line before the rect.  testSupport/pdfPaint.js reads back what a viewer
// really paints and catches this.
//
// Legacy helpers kept for court documents and anything not yet converted:
//   pdfHeader / pdfFooter / pdfBrandRgb / pdfSafeName — unchanged except that
//   pdfFooter's note is now BLACK BOLD instead of red (a red note scans as a
//   faint gray; the court-document warning "DRAFT — NOT FOR FILING" rides in
//   that note and must still stand out), and its "Generated … / firm name /
//   Page N of M" stamp is black (PDF_PLAIN.text) instead of gray 120.

import { firmTz, DEFAULT_FIRM_TZ } from '../utils/eventTime'

// Font-size constants — expressed as pt (jsPDF's setFontSize takes pt).
// Kept together so a firm-wide tweak is one edit.
export const PDF_FONTS = {
  firmName:  16,    // large bold letterhead
  firmMeta:   9,    // address / city / phone line
  title:     13,    // report / document title under the separator
  subtitle:  10,    // second line under the title (matter/context)
  body:       9,    // default body text
  small:      8,    // footer + fine print
}

// Standard y-cursor targets (in mm — jsPDF default unit).  These are the
// vertical positions the pdfHeader helper writes to.  Any generator that
// wants to render content BELOW the header should start at y=52 or later.
export const PDF_LAYOUT = {
  firmY:        18,   // firm name centered
  addressY:     24,
  cityY:        28,
  phoneY:       32,   // optional phone/email line
  separatorY:   36,
  titleY:       44,
  subtitleY:    50,
  contentStart: 56,   // safe y where autoTable / body text can begin
  footerY:      285,  // ~10mm from bottom of A4 (297mm)  — for setFontSize(8)
  marginLR:     14,   // left/right margin used by autoTable + separators
}

// Parse the firm's configured report header color (Settings -> PDF Style,
// stored as firm_settings.report_header_color) into the RGB triple jsPDF's
// fillColor wants.
//
// Takes the WHOLE firmSettings object rather than the hex string because
// every caller fetches settings asynchronously and a user can click Export
// before that resolves, so this must tolerate undefined and fall back rather
// than throw.  Any value that is not 6 hex digits -- unset, empty, 'nonsense',
// a 3-digit shorthand -- yields the caller's fallback, so an unconfigured
// firm sees exactly the color it saw before this helper existed.
//
// Callers supply their OWN fallback rather than sharing one, deliberately:
// the Reports table header has always been near-black [40,40,40] and the
// Attorney Productivity table header has always been purple [80,60,130].
// A shared default would silently restyle one of them for every firm that
// has never opened the PDF Style tab.
//
// Declared above pdfHeader so it sits above every consumer in this file.
export function pdfBrandRgb(firmSettings, fallback = [40, 40, 40]) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((firmSettings && firmSettings.report_header_color) || '')
  if (!m) return fallback
  const h = m[1]
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

// Draw the firm letterhead + document title.  Idempotent per doc — every
// caller passes the same firmSettings so the layout matches everywhere.
//
// Layout (mm, centered):
//     18   {firm name} — 16pt bold
//     24   {address}   — 9pt
//     28   {city, state zip}
//     32   {phone | email}    (optional — only when the settings are set)
//     36   ─────────────────────  separator line
//     44   {title}    — 13pt bold
//     50   {subtitle} — 10pt (optional)
//
// Returns the next-usable y-cursor (56 by default).  If the caller only
// wants the firm block without a title, pass title=null.
export function pdfHeader(doc, opts) {
  const {
    firmSettings = {},
    title = null,
    subtitle = null,
  } = opts || {}
  const pw = doc.internal.pageSize.getWidth()
  const cx = pw / 2

  // Firm name — falls back to a generic label so we don't render an empty
  // header if firm_settings never got saved.
  doc.setFont('times', 'bold')
  doc.setFontSize(PDF_FONTS.firmName)
  doc.text(firmSettings.firm_name || 'Law Firm', cx, PDF_LAYOUT.firmY, { align: 'center' })

  doc.setFont('times', 'normal')
  doc.setFontSize(PDF_FONTS.firmMeta)
  if (firmSettings.address) {
    doc.text(String(firmSettings.address), cx, PDF_LAYOUT.addressY, { align: 'center' })
  }
  const cityLine = [firmSettings.city, firmSettings.state, firmSettings.zip].filter(Boolean).join(', ')
  if (cityLine) {
    doc.text(cityLine, cx, PDF_LAYOUT.cityY, { align: 'center' })
  }
  // Optional phone/email line
  const contact = []
  if (firmSettings.phone) contact.push(`Tel: ${firmSettings.phone}`)
  if (firmSettings.email) contact.push(firmSettings.email)
  if (contact.length) {
    doc.text(contact.join('  |  '), cx, PDF_LAYOUT.phoneY, { align: 'center' })
  }

  // Separator line under the firm block
  doc.setLineWidth(0.4)
  doc.line(PDF_LAYOUT.marginLR, PDF_LAYOUT.separatorY, pw - PDF_LAYOUT.marginLR, PDF_LAYOUT.separatorY)

  // Title + subtitle
  if (title) {
    doc.setFont('times', 'bold')
    doc.setFontSize(PDF_FONTS.title)
    doc.text(String(title), cx, PDF_LAYOUT.titleY, { align: 'center' })
    if (subtitle) {
      doc.setFont('times', 'normal')
      doc.setFontSize(PDF_FONTS.subtitle)
      doc.text(String(subtitle), cx, PDF_LAYOUT.subtitleY, { align: 'center' })
      return PDF_LAYOUT.contentStart
    }
    return PDF_LAYOUT.subtitleY + 6
  }
  return PDF_LAYOUT.titleY + 4
}

// Draw a footer on EVERY page currently in the doc — page numbers on the
// right, generated timestamp on the left, optional firm name centered.
// Call this AFTER all content is written so getNumberOfPages() reflects
// the final count.
//
// Options:
//   { generatedAt: Date | null, showFirmName: boolean, firmSettings, note }
export function pdfFooter(doc, opts = {}) {
  const { generatedAt = new Date(), showFirmName = true, firmSettings = {}, note = null } = opts
  const totalPages = doc.getNumberOfPages()
  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()
  const y = ph - 8

  // Format the timestamp consistently across every PDF (short month, no
  // seconds — keeps the footer tight).  Fallback if the caller passes a
  // stringified date.
  const ts = generatedAt instanceof Date
    ? generatedAt
    : (generatedAt ? new Date(generatedAt) : new Date())
  // On the firm's clock (pdfResolveTimeZone), never the browser's: the
  // privilege log and M&C letter put the firm's date in their header and
  // file name, and this stamp must name the same day.
  const stamp = `Generated ${ts.toLocaleString('en-US', {
    timeZone: pdfResolveTimeZone({ firmSettings }),
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })}`

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)
    doc.setFont('times', 'normal')
    doc.setFontSize(PDF_FONTS.small)
    // BLACK, not gray 120: under the firm's plain-PDF decision (2026-10-13)
    // court documents print in plain colours that scan cleanly, and a gray
    // stamp fades on a scan.  Literal black (= PDF_PLAIN.text): PDF_PLAIN is
    // declared further down this file, so it is not referenced from here.
    doc.setTextColor(0, 0, 0)
    // Left — generated timestamp
    doc.text(stamp, PDF_LAYOUT.marginLR, y)
    // Center — optional firm name for cover-of-page repetition
    if (showFirmName && firmSettings.firm_name) {
      doc.text(String(firmSettings.firm_name), pw / 2, y, { align: 'center' })
    }
    // Right — page N of M
    doc.text(`Page ${p} of ${totalPages}`, pw - PDF_LAYOUT.marginLR, y, { align: 'right' })
    // Optional bottom-center note (e.g. "CONFIDENTIAL", a statute cite, or
    // the court documents' "DRAFT — NOT FOR FILING").  BLACK BOLD, not red
    // (firm decision 2026-10-13): red scans as faint gray, and bold keeps a
    // warning standing out on a black-and-white copy.
    if (note) {
      doc.setFont('times', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(String(note), pw / 2, y - 4, { align: 'center' })
      doc.setFont('times', 'normal')
      doc.setTextColor(0, 0, 0)   // back to the stamp colour (also black)
    }
  }
  // Restore defaults (black text, body size) for the caller
  doc.setTextColor(0, 0, 0)
  doc.setFontSize(PDF_FONTS.body)
}

// ═══ Plain (Amicus-style) helpers — see the API block at the top ═══════════

// mm per typographic point.
const PT_MM = 25.4 / 72

export const PDF_PLAIN = Object.freeze({
  text:        Object.freeze([0, 0, 0]),
  headText:    Object.freeze([0, 0, 0]),
  headFill:    Object.freeze([221, 221, 221]),   // the ONLY fill a plain table paints
  rule:        Object.freeze([150, 150, 150]),   // row separators
  muted:       Object.freeze([85, 85, 85]),      // secondary text that must still scan
  border:      Object.freeze([0, 0, 0]),         // title box, letterhead rule, total rule
  watermark:   Object.freeze([175, 175, 175]),   // court DRAFT stamp: light, still legible
  // Billable Time Chart (the one chart left in a PDF): series fills, darkest
  // first.  Outline every bar in PDF_PLAIN.border so the lightest still shows.
  chartSeries: Object.freeze([
    Object.freeze([60, 60, 60]),
    Object.freeze([150, 150, 150]),
    Object.freeze([215, 215, 215]),
  ]),
  font:        'helvetica',
  rowRule:     0.1,    // mm — thin gray line under every row
  footRule:    0.3,    // mm — black line above the totals row
  boxBorder:   0.3,    // mm — report title box / letterhead rule
})

// Content stops this far above the page's bottom edge.  The footer band
// below holds the "Printed by … / Page: N of M" line (baseline at
// PDF_FOOTER_BASELINE) and the note lines above it; three 8pt note lines
// reach ~22.8 mm up, so 24 keeps a clear gap.
export const PDF_BOTTOM_MARGIN = 24
export const PDF_FOOTER_BASELINE = 10
const FOOTER_SIZE = 8
// Note sizes tried in order; the first at which the wrapped note fits the
// band (footerNoteMaxLines) wins.  6pt is the floor: smaller does not
// survive a scan.
const FOOTER_NOTE_SIZES = [8, 7, 6.5, 6]
const FOOTER_NOTE_GAP = 4          // mm between the note's last baseline and the stamp line
const FOOTER_NOTE_CLEARANCE = 0.5  // mm the note's cap top keeps below the content limit
const FOOTER_STAMP_MIN_SIZE = 4    // pt — floor when a very long name shrinks the stamp

// ═══ PAGE SETUP — internal report PDFs (James 2026-10-29) ═══════════════════
// Report PDFs used A4 with 14 mm sides and a 24 mm bottom band, so a Letter
// printer shrank every page and about an inch of paper went unused.  A doc
// given a page setup (pdfApplyPageSetup, lib/pdfPageSetup.js) prints on US
// Letter with the person's margins — Narrow 0.4" (default), Moderate 0.5" or
// Wide 0.75" on every side — and its "Printed by … / Page: N of M" line sits
// INSIDE the bottom margin, so tables run down to the margin itself.  Every
// helper below reads the doc's geometry; a doc without a setup (invoices,
// receipts, trust statements, court documents, checks) keeps the old layout
// to the millimetre.
//   pdfPageGeometry(doc) → { setup, left, right, top, tableTop, bottom,
//                            footerBaseline, tableDelta }   (mm; bottom and
//                            footerBaseline measured up from the bottom edge)
//   pdfUsableWidth(doc)  → page width between the margins
//   pdfTableFontSize(doc, base, { min, max }) → base + the Table size step
export const PDF_MARGIN_CHOICES = Object.freeze([
  Object.freeze({ id: 'narrow', label: 'Narrow', inches: 0.4 }),
  Object.freeze({ id: 'moderate', label: 'Moderate', inches: 0.5 }),
  Object.freeze({ id: 'wide', label: 'Wide', inches: 0.75 }),
])
// Points added to a table's body size.  "Normal" is the firm's report font
// size (Reports → ⚙️ PDF Style); the fitter still steps down from the result
// whenever the columns would not fit the width.
export const PDF_TABLE_SIZE_CHOICES = Object.freeze([
  Object.freeze({ id: 'smaller', label: 'Smaller', delta: -1 }),
  Object.freeze({ id: 'normal', label: 'Normal', delta: 0 }),
  Object.freeze({ id: 'larger', label: 'Larger', delta: 2 }),
])
export const PDF_DEFAULT_MARGINS = 'narrow'
export const PDF_DEFAULT_TABLE_SIZE = 'normal'
// The footer line's baseline sits this far below the content limit, but never
// closer than 6 mm to the paper's edge (most printers cannot print the outer
// ~4-5 mm) nor farther up than the old 10 mm.
const SETUP_FOOTER_DROP = 4.2
const SETUP_FOOTER_MIN = 6
const LEGACY_GEOMETRY = Object.freeze({
  setup: false, margins: null, tableSize: null,
  left: 14, right: 14, top: 12, tableTop: 14,
  bottom: PDF_BOTTOM_MARGIN, footerBaseline: PDF_FOOTER_BASELINE, tableDelta: 0,
})
const pageSetups = new WeakMap()

export function pdfMarginChoice (id) {
  return PDF_MARGIN_CHOICES.find(c => c.id === id) || PDF_MARGIN_CHOICES.find(c => c.id === PDF_DEFAULT_MARGINS)
}
export function pdfTableSizeChoice (id) {
  return PDF_TABLE_SIZE_CHOICES.find(c => c.id === id) || PDF_TABLE_SIZE_CHOICES.find(c => c.id === PDF_DEFAULT_TABLE_SIZE)
}

// jsPDF constructor options for a report: US Letter, mm, the orientation.
export function pdfReportDocOptions (orientation = 'portrait') {
  return { orientation: orientation === 'landscape' ? 'landscape' : 'portrait', unit: 'mm', format: 'letter' }
}

// Give `doc` a page setup ({ margins, tableSize } ids; unknown ids fall back
// to Narrow / Normal).  Returns the doc, so it chains:
//   const doc = pdfApplyPageSetup(new jsPDF(pdfReportDocOptions(o)), setup)
// { footerNoteLines: n } keeps room above the footer line for an n-line
// pdfPlainFooter note (8 pt): the content limit rises just enough, so the
// note never lands on a table.  Without it a note has no room on a setup doc
// (pdfPlainFooter warns and shrinks it).
export function pdfApplyPageSetup (doc, setup = {}, { footerNoteLines = 0 } = {}) {
  const m = pdfMarginChoice(setup && setup.margins)
  const t = pdfTableSizeChoice(setup && setup.tableSize)
  const mm = Math.round(m.inches * 25.4 * 100) / 100
  const footerBaseline = Math.min(PDF_FOOTER_BASELINE, Math.max(SETUP_FOOTER_MIN, mm - SETUP_FOOTER_DROP))
  const n = Math.max(0, Math.floor(Number(footerNoteLines) || 0))
  const noteRoom = n === 0 ? 0
    : footerBaseline + FOOTER_NOTE_GAP + (n - 1) * FOOTER_SIZE * PT_MM * 1.2 + FOOTER_SIZE * PT_MM * 0.72 + FOOTER_NOTE_CLEARANCE + 0.01
  pageSetups.set(doc, Object.freeze({
    setup: true, margins: m.id, tableSize: t.id,
    left: mm, right: mm, top: mm, tableTop: mm,
    bottom: Math.max(mm, Math.round(noteRoom * 100) / 100),
    footerBaseline,
    tableDelta: t.delta,
  }))
  return doc
}

export function pdfPageGeometry (doc) {
  return (doc && pageSetups.get(doc)) || LEGACY_GEOMETRY
}

export function pdfUsableWidth (doc) {
  const g = pdfPageGeometry(doc)
  return doc.internal.pageSize.getWidth() - g.left - g.right
}

// The body size a table starts from: `base` plus the doc's Table size step,
// kept within [min, max].  A doc without a setup gets `base` unchanged.
export function pdfTableFontSize (doc, base, { min = 6.5, max = 16 } = {}) {
  const b = Number.isFinite(Number(base)) ? Number(base) : PDF_FONTS.body
  const g = pdfPageGeometry(doc)
  if (!g.setup) return b
  return Math.max(min, Math.min(max, b + g.tableDelta))
}

const rgb = c => [c[0], c[1], c[2]]

function isValidTimeZone (tz) {
  if (!tz || typeof tz !== 'string') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date(0))
    return true
  } catch {
    return false
  }
}

// The firm's zone, never the browser's: an attorney printing from Phoenix
// or a laptop left on UTC must not stamp a different day on the report.
export function pdfResolveTimeZone ({ firmTimeZone, firmSettings } = {}) {
  const candidates = [firmTimeZone, firmSettings && firmSettings.timezone, firmTz(), DEFAULT_FIRM_TZ]
  return candidates.find(isValidTimeZone) || DEFAULT_FIRM_TZ
}

// "Tuesday, October 13, 2026 2:15 PM".  Assembled from formatToParts rather
// than format(): ICU 72+ writes "… 2026 at 2:15 PM" and a U+202F narrow
// no-break space before PM, and one non-WinAnsi character switches the whole
// jsPDF run to UTF-16.
export function pdfPrintedStamp (date = new Date(), timeZone) {
  let d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) d = new Date()
  const tz = isValidTimeZone(timeZone) ? timeZone : pdfResolveTimeZone()
  const parts = {}
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d)) {
    if (p.type !== 'literal') parts[p.type] = p.value
  }
  return `${parts.weekday}, ${parts.month} ${parts.day}, ${parts.year} ${parts.hour}:${parts.minute} ${String(parts.dayPeriod || '').toUpperCase()}`.trim()
}

// The signed-in user's display name for "Printed by" (AuthContext's user has
// `name` and `email`).  Empty string, not 'Unknown', when there is none.
export function pdfPrintedByName (user) {
  if (!user) return ''
  return String(user.name || user.email || '').trim()
}

export function pdfContentBottom (doc) {
  return doc.internal.pageSize.getHeight() - pdfPageGeometry(doc).bottom
}

// Manual page-break check for hand-drawn content, against the same bottom
// limit autoTable uses, so text never runs into the footer band.
export function pdfEnsureSpace (doc, y, needed = 0, { top } = {}) {
  if (y + needed > pdfContentBottom(doc)) {
    doc.addPage()
    if (top !== undefined) return top
    const g = pdfPageGeometry(doc)
    return g.setup ? g.top : 20
  }
  return y
}

// Put the pen back to the plain defaults.
export function pdfPlainReset (doc) {
  doc.setFont(PDF_PLAIN.font, 'normal')
  doc.setFontSize(PDF_FONTS.body)
  doc.setTextColor(0, 0, 0)
  doc.setDrawColor(0, 0, 0)
}

// autoTable margin input (number | [t,r,b,l] | [v,h] | object) → object.
function normalizeMargin (m) {
  if (m == null) return {}
  if (typeof m === 'number') return { top: m, right: m, bottom: m, left: m }
  if (Array.isArray(m)) {
    if (m.length >= 4) return { top: m[0], right: m[1], bottom: m[2], left: m[3] }
    if (m.length === 3) return { top: m[0], right: m[1], bottom: m[2], left: m[1] }
    if (m.length === 2) return { top: m[0], right: m[1], bottom: m[0], left: m[1] }
    if (m.length === 1) return normalizeMargin(m[0])
    return {}
  }
  if (typeof m === 'object') {
    const out = { ...m }
    if (typeof m.vertical === 'number') { out.top = m.vertical; out.bottom = m.vertical }
    if (typeof m.horizontal === 'number') { out.left = m.horizontal; out.right = m.horizontal }
    delete out.vertical
    delete out.horizontal
    return out
  }
  return {}
}

export function plainTableStyles (opts = {}) {
  const {
    doc = null,
    fontSize = PDF_FONTS.body,
    font = PDF_PLAIN.font,
    cellPadding,
    styles = {},
    headStyles = {},
    bodyStyles = {},
    footStyles = {},
    margin,
  } = opts
  const base = {
    font,
    fontSize,
    textColor: rgb(PDF_PLAIN.text),
    fillColor: false,
    lineColor: rgb(PDF_PLAIN.rule),
    lineWidth: { bottom: PDF_PLAIN.rowRule },
  }
  if (cellPadding !== undefined) base.cellPadding = cellPadding
  const m = normalizeMargin(margin)
  // A doc with a page setup gets its own margins (top = the margin on every
  // page after the first); without `doc` — or for a doc without a setup —
  // the old 14 / 14 / 24 layout.  An explicit margin still wins, except that
  // the bottom never rises into the footer band.
  const g = pdfPageGeometry(doc)
  return {
    theme: 'plain',
    styles: { ...base, ...styles },
    headStyles: {
      fillColor: rgb(PDF_PLAIN.headFill),
      textColor: rgb(PDF_PLAIN.headText),
      fontStyle: 'bold',
      lineColor: rgb(PDF_PLAIN.rule),
      lineWidth: { bottom: PDF_PLAIN.rowRule },
      ...headStyles,
    },
    bodyStyles: { ...bodyStyles },
    footStyles: {
      fillColor: false,
      textColor: rgb(PDF_PLAIN.text),
      fontStyle: 'bold',
      lineColor: rgb(PDF_PLAIN.border),
      lineWidth: { top: PDF_PLAIN.footRule },
      ...footStyles,
    },
    // No zebra striping.  Explicit, so the theme can never bring one back.
    alternateRowStyles: {},
    margin: {
      top: g.tableTop,
      left: g.left,
      right: g.right,
      ...m,
      bottom: Math.max(g.bottom, Number(m.bottom) || 0),
    },
  }
}

// Wrap `text` at `maxWidth` in the given helvetica style/size; returns
// [{ text, style, size, width }] with widths measured in that font.
function measureLines (doc, text, style, size, maxWidth) {
  doc.setFont(PDF_PLAIN.font, style)
  doc.setFontSize(size)
  return doc.splitTextToSize(String(text), maxWidth).map(line => ({
    text: line, style, size, width: doc.getTextWidth(line),
  }))
}

const subtitleList = subtitle => (Array.isArray(subtitle) ? subtitle : [subtitle])
  .filter(s => s !== null && s !== undefined && String(s).trim() !== '')

export function pdfReportBox (doc, opts = {}) {
  const g = pdfPageGeometry(doc)
  const { firmSettings, title = null, owner = null, subtitle = null, startY = g.top } = opts
  const fs = firmSettings || {}
  const pw = doc.internal.pageSize.getWidth()
  const maxBoxW = pw - g.left - g.right
  const padX = 6
  const padY = 3
  const innerMax = maxBoxW - 2 * padX

  const groups = []
  groups.push(measureLines(doc, fs.firm_name || 'Law Firm', 'italic', 11, innerMax))
  if (owner && String(owner).trim()) groups.push(measureLines(doc, `${String(owner).trim()}'s`, 'normal', 10, innerMax))
  if (title) groups.push(measureLines(doc, title, 'bold', 13, innerMax))
  for (const s of subtitleList(subtitle)) groups.push(measureLines(doc, s, 'bold', 10, innerMax))

  const widest = Math.max(...groups.flat().map(l => l.width))
  const boxW = Math.min(maxBoxW, Math.max(Math.min(110, maxBoxW), widest + 2 * padX))
  const boxX = g.left + (maxBoxW - boxW) / 2
  const cx = boxX + boxW / 2

  // Baselines: ascent ≈ 0.8 em above, ~0.45 em of descent + leading below.
  doc.setTextColor(0, 0, 0)
  let cursor = startY + padY
  groups.forEach((lines, gi) => {
    if (gi > 0) cursor += 0.6
    for (const l of lines) {
      const baseline = cursor + l.size * PT_MM * 0.8
      doc.setFont(PDF_PLAIN.font, l.style)
      doc.setFontSize(l.size)
      doc.text(l.text, cx, baseline, { align: 'center' })
      cursor = baseline + l.size * PT_MM * 0.45
    }
  })
  const boxH = cursor + padY - startY

  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(PDF_PLAIN.boxBorder)
  doc.rect(boxX, startY, boxW, boxH, 'S')

  pdfPlainReset(doc)
  return startY + boxH + 6
}

export function pdfPlainLetterhead (doc, opts = {}) {
  const { firmSettings, title = null, subtitle = null, startY = 16, align = 'center' } = opts
  const fs = firmSettings || {}
  const pw = doc.internal.pageSize.getWidth()
  const g = pdfPageGeometry(doc)
  const left = g.left
  const right = pw - g.right
  const x = align === 'left' ? left : (left + right) / 2
  const textOpts = align === 'left' ? {} : { align: 'center' }

  doc.setTextColor(0, 0, 0)
  let y = startY
  doc.setFont(PDF_PLAIN.font, 'bold')
  doc.setFontSize(14)
  doc.text(String(fs.firm_name || 'Law Firm'), x, y, textOpts)
  let last = y
  y += 5.5

  doc.setFont(PDF_PLAIN.font, 'normal')
  doc.setFontSize(PDF_FONTS.firmMeta)
  const cityLine = [fs.city, [fs.state, fs.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  // "Tel: …  |  Fax: …  |  email" — each part only when set, so a firm with
  // no fax prints "Tel: …  |  email".  Every client document (invoices,
  // receipts, settlement and annual trust statements) gets the fax here, once;
  // none of them may add it again.
  const contact = []
  if (fs.phone) contact.push(`Tel: ${fs.phone}`)
  if (fs.fax) contact.push(`Fax: ${fs.fax}`)
  if (fs.email) contact.push(fs.email)
  for (const line of [fs.address, cityLine, contact.join('  |  ')]) {
    if (!line) continue
    doc.text(String(line), x, y, textOpts)
    last = y
    y += 4
  }

  const ruleY = last + 3
  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(PDF_PLAIN.boxBorder)
  doc.line(left, ruleY, right, ruleY)
  y = ruleY

  if (title) {
    const lines = measureLines(doc, title, 'bold', 12, right - left)
    doc.setFont(PDF_PLAIN.font, 'bold')
    doc.setFontSize(12)
    y += 7
    lines.forEach((l, i) => {
      if (i > 0) y += 5
      doc.text(l.text, x, y, textOpts)
    })
  }
  const subs = subtitleList(subtitle)
  if (subs.length) {
    doc.setFont(PDF_PLAIN.font, 'normal')
    doc.setFontSize(PDF_FONTS.subtitle)
    for (const s of subs) {
      for (const l of doc.splitTextToSize(String(s), right - left)) {
        y += 5
        doc.text(l, x, y, textOpts)
      }
    }
  }

  pdfPlainReset(doc)
  return y + 6
}

// How many note lines of `size` pt fit between the stamp line and the
// content limit: the last note baseline sits FOOTER_NOTE_GAP above the stamp
// baseline, lines are 1.2 em apart, and the top line's cap height (0.72 em
// in Helvetica) must stay FOOTER_NOTE_CLEARANCE below PDF_BOTTOM_MARGIN.
function footerNoteMaxLines (size, g = LEGACY_GEOMETRY) {
  const lead = size * PT_MM * 1.2
  const room = g.bottom - FOOTER_NOTE_CLEARANCE - (g.footerBaseline + FOOTER_NOTE_GAP) - size * PT_MM * 0.72
  return room < 0 ? 0 : 1 + Math.floor(room / lead)
}

export function pdfPlainFooter (doc, opts = {}) {
  const {
    printedBy,
    note = null,
    firmTimeZone = null,
    firmSettings = null,
    printedAt = new Date(),
  } = opts
  const total = doc.getNumberOfPages()
  const g = pdfPageGeometry(doc)
  const left = g.left
  const hasNote = note !== null && note !== undefined && String(note).trim() !== ''

  // null / undefined / false → client document (no stamp).  A string or a
  // user object → internal; an empty name still stamps the time.
  const internal = printedBy !== null && printedBy !== undefined && printedBy !== false
  const who = !internal ? ''
    : typeof printedBy === 'object' ? pdfPrintedByName(printedBy)
      : String(printedBy).trim()
  const when = internal
    ? pdfPrintedStamp(printedAt, pdfResolveTimeZone({ firmTimeZone, firmSettings }))
    : ''
  const stamp = !internal ? null : who ? `Printed by ${who} on ${when}` : `Printed on ${when}`

  // Wrap the note to the page's width (pages can differ in orientation, so
  // cache per width); step the size down rather than let it grow up into
  // the content area.  Never drop a word — notes carry statute citations.
  const noteCache = new Map()
  const wrapNote = width => {
    if (!noteCache.has(width)) {
      let lines = []
      let size = FOOTER_NOTE_SIZES[0]
      let fits = false
      doc.setFont(PDF_PLAIN.font, 'normal')
      for (const s of FOOTER_NOTE_SIZES) {
        doc.setFontSize(s)
        lines = doc.splitTextToSize(String(note), width)
        size = s
        if (lines.length <= footerNoteMaxLines(s, g)) { fits = true; break }
      }
      if (!fits) {
        console.warn(`pdfPlainFooter: the footer note needs ${lines.length} lines at ${size}pt and rises above the footer band into the content area; shorten it. Note: ${String(note).slice(0, 80)}…`)
      }
      noteCache.set(width, { lines, size })
    }
    return noteCache.get(width)
  }

  for (let p = 1; p <= total; p++) {
    doc.setPage(p)
    const pw = doc.internal.pageSize.getWidth()
    const ph = doc.internal.pageSize.getHeight()
    const right = pw - g.right
    const y = ph - g.footerBaseline
    const wrapped = hasNote ? wrapNote(right - left) : null
    const pageLabel = `Page: ${p} of ${total}`
    doc.setFont(PDF_PLAIN.font, 'normal')
    doc.setFontSize(FOOTER_SIZE)
    doc.setTextColor(0, 0, 0)
    doc.text(pageLabel, right, y, { align: 'right' })
    if (stamp) {
      // Keep at least 4 mm between the stamp and "Page: N of M".
      const room = (right - left) - doc.getTextWidth(pageLabel) - 4
      const width = doc.getTextWidth(stamp)
      const size = width > room ? Math.max(FOOTER_STAMP_MIN_SIZE, FOOTER_SIZE * room / width) : FOOTER_SIZE
      doc.setFontSize(size)
      doc.text(stamp, left, y)
    }
    if (wrapped && wrapped.lines.length) {
      const lead = wrapped.size * PT_MM * 1.2
      doc.setFontSize(wrapped.size)
      wrapped.lines.forEach((line, i) => {
        doc.text(line, pw / 2, y - FOOTER_NOTE_GAP - (wrapped.lines.length - 1 - i) * lead, { align: 'center' })
      })
    }
  }
  pdfPlainReset(doc)
}

// Sanitize a filename fragment so we don't produce paths that Windows or
// macOS rejects.  Chars stripped: / \ ? * : " < > | plus control chars +
// leading/trailing dots + spaces collapsed.  Falls back to "document" if
// the input becomes empty after stripping.
export function pdfSafeName(raw) {
  if (!raw) return 'document'
  return String(raw)
    .replace(/[/\\?*:"<>|\x00-\x1f]/g, '')
    .replace(/\.+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100) || 'document'
}
